import { birthdayIssue } from "./date-only.js";
/**
 * Column date orders for contact-import birthdays.
 * `auto` means the column must agree on one order. Canonical `YYYY-MM-DD` is accepted under every choice.
 */
export const CONTACT_IMPORT_BIRTHDAY_FORMATS = ["auto", "DMY", "MDY", "YMD", "YDM"];
export const CONTACT_IMPORT_BIRTHDAY_ORDERS = ["DMY", "MDY", "YMD", "YDM"];
const CANONICAL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const UTC_MIDNIGHT_ISO = /^(\d{4}-\d{2}-\d{2})T00:00:00\.000Z$/;
const DATE_TIME_PREFIX = /^\d{4}-\d{2}-\d{2}T/;
function dateForOrder(digits, order) {
    switch (order) {
        case "DMY":
            return `${digits.slice(4, 8)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`;
        case "MDY":
            return `${digits.slice(4, 8)}-${digits.slice(0, 2)}-${digits.slice(2, 4)}`;
        case "YMD":
            return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
        case "YDM":
            return `${digits.slice(0, 4)}-${digits.slice(6, 8)}-${digits.slice(4, 6)}`;
        default: {
            const exhaustive = order;
            return exhaustive;
        }
    }
}
function survivingOrders(digits, now) {
    return CONTACT_IMPORT_BIRTHDAY_ORDERS.filter((order) => birthdayIssue(dateForOrder(digits, order), now) == null);
}
function inspectBirthdayCell(value, now) {
    const trimmed = value.trim();
    if (trimmed.length === 0)
        return { type: "empty" };
    const midnight = UTC_MIDNIGHT_ISO.exec(trimmed);
    const canonical = midnight?.[1] ?? (CANONICAL_DATE.test(trimmed) ? trimmed : null);
    if (canonical) {
        return birthdayIssue(canonical, now) == null
            ? { type: "accepted", birthday: canonical }
            : { type: "skipped" };
    }
    if (DATE_TIME_PREFIX.test(trimmed))
        return { type: "skipped" };
    const digits = trimmed.replace(/[\s/.\-]/g, "");
    if (!/^\d{8}$/.test(digits))
        return { type: "skipped" };
    const orders = survivingOrders(digits, now);
    return orders.length > 0 ? { type: "orders", digits, orders } : { type: "skipped" };
}
function inferBirthdayOrder(values, now) {
    const votes = new Set();
    let sawMultiOrderCell = false;
    for (const value of values) {
        const cell = inspectBirthdayCell(value, now);
        if (cell.type !== "orders")
            continue;
        if (cell.orders.length === 1) {
            const order = cell.orders[0];
            if (order)
                votes.add(order);
            continue;
        }
        sawMultiOrderCell = true;
    }
    if (votes.size === 1) {
        const format = [...votes][0];
        if (!format)
            return { kind: "ambiguous" };
        return { kind: "resolved", format };
    }
    if (votes.size > 1 || sawMultiOrderCell)
        return { kind: "ambiguous" };
    return { kind: "open" };
}
function resolveCell(value, format, now) {
    const cell = inspectBirthdayCell(value, now);
    if (cell.type === "empty")
        return { birthday: null, skipped: false };
    if (cell.type === "accepted")
        return { birthday: cell.birthday, skipped: false };
    if (cell.type === "skipped" || format == null || !cell.orders.includes(format)) {
        return { birthday: null, skipped: true };
    }
    return { birthday: dateForOrder(cell.digits, format), skipped: false };
}
/**
 * Decide one calendar date per birthday cell.
 * Does not use `Date.parse`, `new Date(string)`, or `Number` on the raw cell.
 */
export function resolveContactImportBirthdayColumn(values, choice = "auto", now = new Date()) {
    const inferred = inferBirthdayOrder(values, now);
    const ambiguous = inferred.kind === "ambiguous";
    const inferredFormat = inferred.kind === "resolved" ? inferred.format : null;
    const format = choice === "auto" ? inferredFormat : choice;
    return {
        ambiguous,
        inferredFormat,
        birthdays: values.map((value) => resolveCell(value, format, now)),
    };
}
//# sourceMappingURL=contact-import-birthday.js.map